import { normalizeString } from "./excelfunction";

/**
 * Trouve les clés des colonnes Nom et Prénom dans les en-têtes
 * @param {Object|Array} row - Ligne Excel ou headers
 * @returns {{ nomKey: string|null, prenomKey: string|null }}
 */
export const findNomPrenomKeys = (row) => {
    const keys = Array.isArray(row) ? row : Object.keys(row);

    const nomKey = keys.find(key => {
        const normalized = normalizeString(key).toLowerCase().trim();
        return normalized === 'nom' ||
               normalized === 'nom de l\'eleve' ||
               normalized === 'nom de l eleve' ||
               normalized === 'nom eleve' ||
               normalized === 'nom de famille' ||
               normalized === 'nom élève';
    });

    const prenomKey = keys.find(key => {
        const normalized = normalizeString(key).toLowerCase().trim();
        return normalized === 'prénom' ||
               normalized === 'prenom' ||
               normalized === 'prenom eleve' ||
               normalized === 'prénom eleve' ||
               normalized === 'prenom élève' ||
               normalized === 'prénom élève';
    });

    return { nomKey, prenomKey };
};

/**
 * Trouve la clé de la colonne Classe dans les en-têtes
 * @param {Object|Array} row - Ligne Excel ou headers
 * @returns {string|null}
 */
export const findClasseKey = (row) => {
    const keys = Array.isArray(row) ? row : Object.keys(row);
    return keys.find(key => normalizeString(key).toLowerCase() === 'classe');
};

/**
 * Extrait le nom et le prénom d'une ligne Excel
 * @param {Object} row - Ligne Excel
 * @param {string} nomKey - Clé de la colonne Nom
 * @param {string} prenomKey - Clé de la colonne Prénom
 * @returns {{ nom: string, prenom: string }}
 */
export const extractNomPrenom = (row, nomKey, prenomKey) => {
    const nom = String(row[nomKey] || '').trim();
    const prenom = String(row[prenomKey] || '').trim();
    return { nom, prenom };
};

// ========== VALIDATION FICHIERS QUESTIONS (STEP 2) ==========
//
// Les tests spécifiques (09/10/2026) : on cherche les COLONNES d'items, pas les feuilles. Une colonne
// d'item s'écrit « <test> Q<n> - réponse » ou « <test> Q<n> - score » (accents, casse, espaces et zéros
// du numéro ignorés). Une feuille sans colonne d'item est ignorée : note de lecture, scores par
// sous-domaine, ou toute feuille que la plateforme ajoutera. Le nom du test se compare EN ENTIER aux
// tests de l'année du niveau (config.expectedQuestions : `entete`, sinon `displayName`), jamais par
// « commence par ». Un test inconnu, absent, incomplet, en double ou au-delà de son nombre d'items
// bloque l'import (décision du prof : on bloque, et on met la configuration à jour année par année).
// L'import de l'établissement (utils/importEvalNat, fenêtre ModalImportEvalNatChef), fait par le chef ou le
// professeur délégué, passe par ici ; un professeur seul n'importe pas (09/10/2026).

const COLONNE_ITEM = /^(.+?)\s+q0*(\d+)\s*-\s*(reponse|score)$/;
const COLONNE_ITEM_BRUTE = /^(.+?)\s+Q0*\d+\s*[-–—]\s*(?:r[ée]ponse|score)\s*$/i;

/** Un en-tête comparable : sans accents, en minuscules, tirets et espaces unifiés. */
const enteteComparable = (s) => normalizeString(String(s ?? '')).toLowerCase().replace(/[–—]/g, '-');

/** Le numéro d'item sur deux chiffres, comme les questions nationales en base : 1 → « Q01 ». */
export const numeroItem = (n) => `Q${String(Number(n)).padStart(2, '0')}`;

/**
 * Lit un en-tête de colonne d'item : `{ test, testBrut, num, type }`, ou null si ce n'en est pas une.
 * `test` est comparable (sans accents, en minuscules), `testBrut` tel qu'écrit dans le fichier.
 */
export const lireColonneItem = (entete) => {
    const m = enteteComparable(entete).match(COLONNE_ITEM);
    if (!m) return null;
    const brut = String(entete).trim().match(COLONNE_ITEM_BRUTE);
    return {
        test: m[1].trim(),
        testBrut: brut ? brut[1].trim() : m[1].trim(),
        num: numeroItem(m[2]),
        type: m[3] === 'score' ? 'score' : 'reponse',
    };
};

/** Les tests de l'année d'un niveau : `{ categoryId, displayName, entete, count }`. */
export const testsDeLAnnee = (config) => Object.entries(config?.expectedQuestions || {}).map(([categoryId, t]) => ({
    categoryId, displayName: t.displayName, entete: t.entete || t.displayName, count: t.count,
}));

/** Les feuilles qui portent au moins une colonne d'item ; les autres sont ignorées. */
export const feuillesDeTests = (feuilles) => (feuilles || []).filter((f) =>
    Array.isArray(f) && f.length > 0 && Object.keys(f[0] || {}).some((h) => lireColonneItem(h)));

/**
 * Identifie les colonnes d'une feuille de test spécifique.
 * @param {Object} headers - La première ligne de la feuille (ses clés sont les en-têtes)
 * @param {Object} config - Configuration EVAL_NAT_CONFIG pour le niveau/matière
 * @returns {{ scoreColumns: Array<{ key, category, displayName, type, num }>,
 *   inconnues: Array<{ key, testBrut, num }>, classe: string|null, nom: string|null, prenom: string|null }}
 */
export const identifyColumnsQuestions = (headers, config) => {
    const columnMap = { scoreColumns: [], inconnues: [], classe: null, nom: null, prenom: null };
    if (!headers) return columnMap;

    const { nomKey, prenomKey } = findNomPrenomKeys(headers);
    if (nomKey) columnMap.nom = nomKey;
    if (prenomKey) columnMap.prenom = prenomKey;

    const tests = testsDeLAnnee(config);
    Object.keys(headers).forEach((key) => {
        if (normalizeString(key).toLowerCase().trim() === 'classe') columnMap.classe = key;
        const item = lireColonneItem(key);
        if (!item) return;
        const test = tests.find((t) => enteteComparable(t.entete) === item.test);
        if (!test) {
            columnMap.inconnues.push({ key, testBrut: item.testBrut, num: item.num });
            return;
        }
        columnMap.scoreColumns.push({ key, category: test.categoryId, displayName: test.displayName, type: item.type, num: item.num });
    });

    return columnMap;
};

/**
 * Valide le fichier questions : ses feuilles de tests (les autres sont ignorées) et les tests de l'année.
 * @param {Array} sheetsData - Toutes les feuilles du fichier, telles que lues
 * @param {Object} config - Configuration EVAL_NAT_CONFIG
 * @param {string} classeNom - Nom de la classe attendue
 * @returns {{ errors: Array<string>, columnMaps: Array<Object>, sheets: Array }} `sheets` : les feuilles de
 *   tests retenues, dans l'ordre du fichier ; `columnMaps[i]` est celui de `sheets[i]`. Les validations
 *   suivantes et le rangement travaillent sur `sheets`.
 */
export const validateQuestionsFile = (sheetsData, config, classeNom) => {
    const errors = [];
    const columnMaps = [];
    const sheets = feuillesDeTests(sheetsData);

    if (sheets.length === 0) {
        errors.push("Aucune feuille de test spécifique : aucune colonne de la forme « Lexique Q01 - score » n'a été trouvée.");
        return { errors, columnMaps, sheets };
    }

    const tests = testsDeLAnnee(config);
    const vues = new Map();      // « test|num|type » → nombre de colonnes
    const inconnus = new Map();  // test tel qu'écrit dans le fichier → numéros de ses items

    sheets.forEach((sheet, index) => {
        const columnMap = identifyColumnsQuestions(sheet[0], config);
        columnMaps.push(columnMap);

        // Vérifier les colonnes de base
        if (!columnMap.classe) errors.push(`Feuille ${index + 1} : Colonne 'Classe' manquante`);
        if (!columnMap.nom) errors.push(`Feuille ${index + 1} : Colonne 'Nom' manquante`);
        if (!columnMap.prenom) errors.push(`Feuille ${index + 1} : Colonne 'Prénom' manquante`);

        columnMap.scoreColumns.forEach(({ displayName, num, type }) => {
            const cle = `${displayName}|${num}|${type}`;
            vues.set(cle, (vues.get(cle) || 0) + 1);
        });
        columnMap.inconnues.forEach(({ testBrut, num }) => {
            if (!inconnus.has(testBrut)) inconnus.set(testBrut, new Set());
            inconnus.get(testBrut).add(num);
        });

        // Vérifier que tous les élèves sont de la même classe
        if (columnMap.classe && classeNom) {
            const classesInSheet = new Set(sheet.map(row => String(row[columnMap.classe] || '')));
            if (classesInSheet.size > 1) {
                errors.push(`Feuille ${index + 1} : Plusieurs classes détectées (${Array.from(classesInSheet).join(', ')})`);
            } else if (!classesInSheet.has(classeNom)) {
                errors.push(`Feuille ${index + 1} : La classe (${Array.from(classesInSheet)[0]}) ne correspond pas à ${classeNom}`);
            }
        }
    });

    const attendus = tests.map((t) => `« ${t.entete} »`).join(', ');
    inconnus.forEach((nums, test) => {
        errors.push(`Test non reconnu : « ${test} » (${nums.size} items). Tests attendus cette année : ${attendus}.`);
    });
    vues.forEach((n, cle) => {
        if (n < 2) return;
        const [test, num, type] = cle.split('|');
        errors.push(`Colonne en double : « ${test} ${num} - ${type === 'score' ? 'score' : 'réponse'} » (${n} fois).`);
    });
    tests.forEach((t) => {
        const numeros = Array.from({ length: t.count }, (_, i) => numeroItem(i + 1));
        const sansScore = numeros.filter((num) => !vues.has(`${t.displayName}|${num}|score`));
        if (sansScore.length === t.count) {
            errors.push(`Test absent du fichier : « ${t.entete} » (${t.count} items attendus).`);
        } else if (sansScore.length > 0) {
            errors.push(`Test incomplet : « ${t.entete} », pas de score pour ${sansScore.join(', ')}.`);
        }
        const enTrop = [...new Set([...vues.keys()]
            .map((cle) => cle.split('|'))
            .filter(([test, num]) => test === t.displayName && !numeros.includes(num))
            .map(([, num]) => num))];
        if (enTrop.length > 0) {
            errors.push(`Test « ${t.entete} » : ${enTrop.join(', ')} au-delà des ${t.count} items attendus.`);
        }
    });

    return { errors, columnMaps, sheets };
};

/**
 * Les réponses d'un élève (une ligne d'une feuille de test), rangées comme l'affichage les lit :
 * `{ [displayName]: { Q01: { score, réponse } } }`. Une colonne inconnue n'est jamais rangée.
 */
export const reponsesParItem = (row, columnMap) => {
    const reponses = {};
    (columnMap?.scoreColumns || []).forEach(({ key, displayName, type, num }) => {
        const test = reponses[displayName] || (reponses[displayName] = {});
        const item = test[num] || (test[num] = {});
        item[type === 'score' ? 'score' : 'réponse'] = row[key];
    });
    return reponses;
};

/** Ajoute à `reponses` (en place) les items rangés d'une autre feuille du même élève. */
export const ajouterReponses = (reponses, ajout) => {
    Object.entries(ajout).forEach(([test, items]) => {
        reponses[test] = { ...(reponses[test] || {}), ...items };
    });
    return reponses;
};

/**
 * Vérifie que chaque élève dispose de scores valides
 * @param {Array} sheetsData - Tableau de feuilles Excel
 * @param {Array<Object>} columnMaps - Tableau de columnMap par feuille
 * @returns {Array<string>} - Liste des erreurs
 */
export const validateScores = (sheetsData, columnMaps) => {
    const errors = [];
    if (!sheetsData || sheetsData.length === 0 || !columnMaps) {
        return errors;
    }

    sheetsData.forEach((sheet, sheetIndex) => {
        if (!sheet || sheet.length === 0) return;
        const columnMap = columnMaps[sheetIndex];
        if (!columnMap || columnMap.scoreColumns.length === 0) return;

        // Vérifier que CHAQUE élève a TOUS les scores requis
        sheet.forEach((item, index) => {
            const { nom, prenom } = extractNomPrenom(item, columnMap.nom, columnMap.prenom);
            const eleveIdentifier = `${nom} ${prenom}`;
            const ligneExcel = index + 2;

            const scoresMissing = [];
            columnMap.scoreColumns.forEach(scoreCol => {
                const scoreValue = item[scoreCol.key];

                // Vérifier si la valeur est vide
                if (scoreValue === undefined || scoreValue === null || scoreValue === '') {
                    // IMPORTANT: Seules les colonnes de type 'score' sont obligatoires
                    // Les colonnes 'réponse' peuvent être vides
                    if (scoreCol.type === 'score') {
                        scoresMissing.push(scoreCol.key);
                    }
                } else {
                    // Validation du TYPE selon le type de colonne
                    if (scoreCol.type === 'score') {
                        // Les colonnes "score" doivent être des nombres
                        if (typeof scoreValue !== 'number' || isNaN(scoreValue)) {
                                  errors.push(`Feuille ${sheetIndex + 1}, Ligne ${ligneExcel} - ${eleveIdentifier} : Score invalide pour "${scoreCol.key}"`);
                        }
                    }
                    // Les colonnes "reponse" peuvent contenir du texte, donc pas de validation de type
                }
            });

            // Calculer le nombre total de colonnes 'score' obligatoires
            const totalScoreColumns = columnMap.scoreColumns.filter(col => col.type === 'score').length;
            if (scoresMissing.length > 0) {
                errors.push(`Feuille ${sheetIndex + 1}, Ligne ${ligneExcel} - ${eleveIdentifier} : ${scoresMissing.length} score(s) manquant(s) sur ${totalScoreColumns}`);
            }
        });
    });

    return errors;
};

/**
 * Vérifie les doublons (même nom/prénom) au sein d'une même feuille
 * @param {Array} data - Données de la feuille Excel
 * @param {Object|null} columnMap - Mapping des colonnes (optionnel)
 * @returns {Array<string>} - Liste des erreurs
 */
export const checkForDuplicates = (data, columnMap = null) => {
    const errors = [];
    const duplicates = [];
    const seen = new Map();

    // Fonction pour extraire nom et prénom
    const getNomPrenom = (row, index) => {
        let nom, prenom;

        if (columnMap && columnMap.nom && columnMap.prenom) {
            // Utiliser le columnMap fourni (themes ou questions)
            ({ nom, prenom } = extractNomPrenom(row, columnMap.nom, columnMap.prenom));
        } else {
            // Fallback si pas de columnMap
            const { nomKey, prenomKey } = findNomPrenomKeys(row);
            ({ nom, prenom } = extractNomPrenom(row, nomKey, prenomKey));
        }

        // Normaliser (enlever accents et mettre en majuscules)
        const normalizedNom = nom.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
        const normalizedPrenom = prenom.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
        const key = `${normalizedNom}_${normalizedPrenom}`;

        return { nom, prenom, key, index };
    };

    // Parcourir tous les élèves
    data.forEach((row, index) => {
        const { nom, prenom, key } = getNomPrenom(row, index);

        // Vérifier si on a déjà vu cette combinaison
        if (seen.has(key)) {
            const firstIndex = seen.get(key);

            // Ajouter le premier élève trouvé s'il n'est pas déjà dans la liste
            if (!duplicates.some(d => d.index === firstIndex)) {
                const firstRow = getNomPrenom(data[firstIndex], firstIndex);
                duplicates.push({
                    nom: firstRow.nom,
                    prenom: firstRow.prenom,
                    ligne: firstIndex + 2, // +2 car Excel commence à 1 et a une ligne d'en-tête
                    index: firstIndex
                });
            }

            // Ajouter l'élève actuel
            duplicates.push({
                nom: nom,
                prenom: prenom,
                ligne: index + 2,
                index: index
            });
        } else {
            // Première fois qu'on voit cette combinaison
            seen.set(key, index);
        }
    });

    // Créer les messages d'erreur
    if (duplicates.length > 0) {
        errors.push(`⚠️ Doublons détectés (${duplicates.length} élève(s) en double) :`);
        duplicates.forEach(dup => {
            errors.push(`  • Ligne ${dup.ligne} : ${dup.prenom} ${dup.nom}`);
        });
    }

    return errors;
};
